/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import draftApi from '../../../../api/draft.api';
import { ATTACHMENT_CONFIG_ATTRIBUTES, attachmentTypes, hasFileName, reachedUploadLimit } from '../services/constants';
import { useAttachmentsConfig } from '../hooks/useAttachmentsConfig';
import { showAlertError } from '../../../../redux/features/alerts/alerts.slice';
import { createMessage } from '../../../../redux/features/alerts/alerts.helper';
import { getMessageFromError } from '../../../../redux/helpers/thunk.helpers';
import { useAttachmentMetaFields } from './meta/useAttachmentMetaFields';
import {
  getAttachmentConfig,
  getAttachmentConfigValue,
  getDefaultSubtype,
  mapToFormData,
} from '../services/attachments.helper';
import { uiSchemaProperties } from '../../../../shared/enums/uiSchemaProperties';
import {
  increaseLoadingAttachmentsCount,
  decreaseLoadingAttachmentsCount,
} from '../../../../redux/features/submission/submission.slice';

/**
 * Hook for the attachment uploader.
 *
 * @param draftId - parent submission id
 * @param attachmentsUiSchema
 * @param attachmentType - attachment root type
 * @param metaFieldNames - array with the names of all the meta fields of the attachment type
 * @param allowMultipleFiles - flag for single / multiple uploadable files
 * @param unsupportedAttachmentTypes - excluded subtypes
 * @param attachmentsMetadata - RJSF form attachments data
 * @param mergedAttachmentsMetadata - uploaded + pending upload attachments metadata
 * @param onAttachmentUploaded
 * @param onFileDropped - adds the file to the dropzone files
 * @param onDroppedFileRemoved removes the file from the dropzone files
 * @returns
 */
export function useAttachmentUploader({
  draftId,
  attachmentsUiSchema,
  attachmentType,
  metaFieldNames = [],
  allowMultipleFiles,
  unsupportedAttachmentTypes,
  attachmentsMetadata,
  mergedAttachmentsMetadata,
  onAttachmentUploaded,
  onFileDropped,
  onDroppedFileRemoved,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const metaForm = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
  });

  const { getValues, setValue, trigger, watch } = metaForm;
  const [droppedFiles, setDroppedFiles] = useState(allowMultipleFiles ? [] : null);
  const [fileErrorMsg, setFileErrorMsg] = useState([]);

  const isUploaderTitleHidden = attachmentsUiSchema[uiSchemaProperties.ATTACHMENTS_UPLOADER_TITLE_HIDDEN];
  const isUploaderSubtitleVisible = attachmentsUiSchema[uiSchemaProperties.ATTACHMENTS_UPLOADER_SUBTITLE_VISIBLE];

  const metaFieldValues = {};
  metaFieldNames.forEach((metaFieldName) => {
    metaFieldValues[metaFieldName] = watch(metaFieldName, '');
  });

  const { attachmentTypesConfig, attachmentsConfigReady } = useAttachmentsConfig(unsupportedAttachmentTypes);

  const defaultAttachmentSubtype =
    getDefaultSubtype(attachmentTypesConfig, attachmentType, attachmentsUiSchema) || watch('attachmentSubType', '');
  const defaultAttachmentConfig = getAttachmentConfig(attachmentTypesConfig, attachmentType, defaultAttachmentSubtype);

  // Just temporary, we need to move this to Redux
  const [isLoading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { extractAttachmentMetadata } = useAttachmentMetaFields();

  const areFilesDropped = () => {
    return !!droppedFiles && droppedFiles.length > 0;
  };

  const isDropzoneDisabled = () => {
    return !defaultAttachmentSubtype || maxNoOfFilesUploaded();
  };

  const maxNoOfFilesUploaded = () => {
    const maxNumberOfFiles = getAttachmentConfigValue(
      defaultAttachmentConfig,
      ATTACHMENT_CONFIG_ATTRIBUTES.MAX_NUMBER_OF_FILES,
    );
    return attachmentsMetadata.length >= maxNumberOfFiles;
  };

  const onRemoveDroppedFile = async (originalFileName) => {
    if (!allowMultipleFiles) {
      _.delay(() => setDroppedFiles([]), 100);
    } else {
      const deletedFileIndex = droppedFiles.findIndex(file => file.name === originalFileName);
      const modifiedFilesArray = droppedFiles;
      modifiedFilesArray.splice(deletedFileIndex, 1);
      setDroppedFiles(modifiedFilesArray);
    }
    onDroppedFileRemoved(originalFileName);
    closeAlert();
  };

  const uploadFilesToServer = async (files) => {
    const data = mapToFormData(files, metaFieldNames);
    setLoading(true);
    dispatch(increaseLoadingAttachmentsCount());
    const options = {
      onSuccess: onUploadSuccess,
      onError: onUploadError,
      onUploadProgress(progressEvent) {
        setProgress(Math.round(progressEvent.progress * 100));
      },
    };
    await callApi(draftApi.uploadAttachment, options)(draftId, data, options);
  };

  const onUploadSuccess = (attachments) => {
    for (const attachment of attachments) {
      // for bio and payment attachments we have only one subtype so we keep it
      // for other attachments we have multiple subtypes so we are able to select it
      // from the dropdown
      if (attachmentType !== attachmentTypes.PAYMENT) {
        setValue('attachmentSubType', '');
      }
      metaFieldNames.forEach((metaFieldName) => {
        setValue(metaFieldValues[metaFieldName], null);
      });
      onRemoveDroppedFile(attachment.originalFileName);
      onAttachmentUploaded(attachment);
      setProgress(0);
      setLoading(false);
      dispatch(decreaseLoadingAttachmentsCount());
    }
  };

  const onUploadError = async (err) => {
    if (Array.isArray(err.data)) {
      for (const data of err.data) {
        if (data.validation) {
          const fileErrorMsgKeys = Object.keys(data.validation.ERROR);
          setFileErrorMsg(fileErrorMsgKeys);
        } else {
          setFileErrorMsg([await getMessageFromError(err, null)]);
        }
      }

      setLoading(false);
      dispatch(decreaseLoadingAttachmentsCount());
      return;
    }

    if (err.data?.validation) {
      const fileErrorMsgKeys = Object.keys(err.data.validation.ERROR);
      setFileErrorMsg(fileErrorMsgKeys);
    } else if (err.response?.data?.validation) {
      const fileErrorMsgKeys = Object.keys(err.response.data.validation.ERROR);
      setFileErrorMsg(fileErrorMsgKeys);
    } else {
      setFileErrorMsg([await getMessageFromError(err, null)]);
    }
    setLoading(false);
    dispatch(decreaseLoadingAttachmentsCount());
  };

  const onConfirmUpload = async (jsonSchema) => {
    let result;
    setFileErrorMsg([]);
    if (droppedFiles) {
      result = await trigger();
    }

    if (result) {
      const droppedFilesMetadata = [];
      for (const fileItem of droppedFiles) {
        const file = extractAttachmentMetadata(fileItem, getValues, jsonSchema);
        if (file) droppedFilesMetadata.push(file);
      }

      if (droppedFilesMetadata.length > 0) await uploadFilesToServer(droppedFilesMetadata);
    }
  };

  const onDropFile = (dropZoneFiles) => {
    const [dropZoneFile] = dropZoneFiles;

    if (dropZoneFile) {
      if (reachedUploadLimit(mergedAttachmentsMetadata, dropZoneFile)) {
        dispatch(showAlertError(createMessage(t('attachments.warning.maximum-size-reached'))));
        return false;
      }
      if (hasFileName(mergedAttachmentsMetadata, dropZoneFile.name)) {
        dispatch(showAlertError(createMessage(t('attachments.warning.same_filename'))));
        return false;
      }

      dropZoneFile.byteSize = dropZoneFile.size;
      dropZoneFile.attachmentType = defaultAttachmentSubtype;
      dropZoneFile.attachmentSubType = attachmentType;
      metaFieldNames.forEach((metaFieldName) => {
        dropZoneFile[metaFieldName] = metaFieldValues[metaFieldName];
      });

      if (allowMultipleFiles) {
        const updatedDropFiles = droppedFiles?.length > 0 ? droppedFiles : [];
        updatedDropFiles.push(dropZoneFile);
        setDroppedFiles(updatedDropFiles);
      } else {
        setDroppedFiles([dropZoneFile]);
      }
      onFileDropped(dropZoneFile, allowMultipleFiles);

      return true;
    }
    return false;
  };

  const closeAlert = () => {
    setFileErrorMsg([]);
  };

  return {
    metaForm,
    attachmentsConfigReady,
    defaultAttachmentSubtype,
    defaultAttachmentConfig,

    droppedFiles,
    onDropFile,
    onRemoveDroppedFile,
    areFilesDropped,
    isDropzoneDisabled,
    maxNoOfFilesUploaded,
    isUploaderTitleHidden,
    isUploaderSubtitleVisible,

    onConfirmUpload,
    isLoading,
    progress,
    fileErrorMsg,
    closeAlert,
  };
}
